import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingBag, ArrowRight, X } from "lucide-react";

const fmtDuration = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

/** Sticky selection panel on Booking Wizard step 1 (mobile bar + desktop sidebar). */
export default function SelectionPanel({ selectedServices, onRemove, onNext, getServiceTotal, canProceed = true, missingSelections = [] }) {
  const totalPrice = selectedServices.reduce((s, sv) => s + (getServiceTotal ? getServiceTotal(sv) : sv.price), 0);
  const totalDuration = selectedServices.reduce((s, sv) => s + sv.duration_minutes, 0);

  const mobileBar = selectedServices.length > 0 && (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border shadow-[0_-4px_20px_rgba(43,18,27,0.12)] px-4 py-3">
      <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground">{selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''} · {fmtDuration(totalDuration)}</p>
          <p className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>£{totalPrice.toFixed(2)}</p>
        </div>
        <Button onClick={onNext} className="rounded-xl px-6" data-testid="step1-next-mobile"
          disabled={!canProceed}
        >
          Next <ArrowRight size={14} className="ml-1" />
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {mobileBar}
      <div className="hidden lg:block w-72 shrink-0">
        <div className="sticky top-24">
          <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.10)] overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center gap-2">
              <ShoppingBag size={15} className="text-primary" />
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>Your Selection</h3>
            </div>

            {selectedServices.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-xs text-muted-foreground">No services selected yet</p>
                <p className="text-[10px] text-muted-foreground mt-1">Choose a treatment to get started</p>
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {selectedServices.map(svc => (
                  <div key={svc.id} className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium leading-tight">{svc.name}</p>
                      <p className="text-[10px] text-muted-foreground">{svc.duration_minutes} min</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-xs font-semibold text-primary">£{(getServiceTotal ? getServiceTotal(svc) : svc.price).toFixed(2)}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); onRemove(svc); }}
                        className="w-4 h-4 rounded-full bg-muted hover:bg-red-100 hover:text-red-600 flex items-center justify-center transition-colors"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="border-t border-border pt-2 mt-1 space-y-1">
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>Duration</span>
                    <span>{fmtDuration(totalDuration)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="text-sm font-semibold text-primary">£{totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="px-4 pb-4">
              <Button
                onClick={onNext}
                disabled={selectedServices.length === 0 || !canProceed}
                className="w-full rounded-xl"
                data-testid="step1-next-desktop"
              >
                {selectedServices.length === 0 ? 'Select a service' : 'Continue'} <ArrowRight size={14} className="ml-1" />
              </Button>
              {!canProceed && selectedServices.length > 0 && (
                <p className="text-[11px] text-amber-600 text-center mt-2 font-medium">
                  Please select nail length to continue
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
